import React, { useEffect, useContext, useState } from 'react'
import * as Yup from 'yup'
import Personnummer from 'personnummer'
import XLSX from 'xlsx'
import IStatistics, {
  IStatisticsData,
  IStatisticsArrayData,
  IStatisticsSelectData,
} from './statistics.interface'
import StatisticsContext from './statistics-context'
import DashboardContext from '../layout/dashboard-context'
import Spinner from '../spinner/spinner'
import api from '../../api'
import StatisticsInfo from './statistics-info'
import {
  originalData,
  fieldsOrder,
  emptyStatisticsData,
  validationSchema,
} from './statistics-helper'
import EditModal from './edit-modal'
// import { Persist } from 'formik-persist'
import {
  faDownload,
  faUpload,
  faTrash,
  faCheckCircle,
  faXmarkCircle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'
import Modal from 'react-modal'

const CoverEverythingSpinner = () => {
  return (
    <div className="fixed left-0 top-0 w-full h-full opacity-50 bg-white z-50 flex justify-center items-center">
      <Spinner />
    </div>
  )
}

interface StatisticsProps {
  setStep: (step: number) => void
}

const Statistics: React.FC<StatisticsProps & IStatistics> = ({ setStep }: StatisticsProps) => {
  const { received, setReceived } = useContext(StatisticsContext)
  const { selectedOrganization } = useContext(DashboardContext)
  const [fetchingData, setFetchingData] = useState<boolean>(true)
  const [alreadySentStatistics, setAlreadySentStatistics] = useState<boolean>(false)
  const [shouldNotSendStatistics, setShouldNotSendStatistics] = useState<boolean>(false)
  const [selectedIndex, setSelectedIndex] = useState<any>(0)
  const [latestIndex, setLatestIndex] = useState<number>(0)
  const [currentData, setCurrentData] = useState<IStatisticsArrayData>(originalData)
  const [outsideValues, setOutsideValues] = useState<IStatisticsData>(emptyStatisticsData)
  const [scb, setScb] = useState<boolean>(false)
  const [hasInvalidData, setHasInvalidData] = useState<boolean>(false)
  const [hasValidData, setHasValidData] = useState<boolean>(false)
  const [displayForm, setDisplayForm] = useState<boolean>(false)
  const [showImportErrorMsg, setShowImportErrorMsg] = useState<boolean>(false)
  const [showImportSuccessMsg, setShowImportSuccessMsg] = useState<boolean>(false)
  const [uploadMessage, setUploadMessage] = useState<string>('')
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>('')
  const [showSubmitModal, setShowSubmitModal] = useState<boolean>(false)
  const [extraConfirmationEmail, setExtraConfirmationEmail] = useState<string>('')
  const [extraEmailSent, setExtraEmailSent] = useState<boolean>(false)
  const [extraEmailSentError, setExtraEmailSentError] = useState<boolean>(false)

  const setter = null
  const resetter = null
  const errorObject = {}
  const submitter = null
  const downloadSCBMalllink ='https://fastigo.se/wp-content/uploads/2025/09/excelmall_statistik_scb.xlsx'
  const downloadNoSCBMalllink = 'https://fastigo.se/wp-content/uploads/2025/09/excelmall_statistik_ejscb.xlsx'
  const downloadSCBHelplink = 'https://fastigo.se/wp-content/uploads/2020/08/anvisningar_statistik_scb.pdf'
  const downloadNoSCBHelplink =
    'https://fastigo.se/wp-content/uploads/2020/08/anvisningar_statistik_ejscb.pdf'
  const navigate = useNavigate()

  const scrollToFirstErrorField = () => {
    const errorFound = fieldsOrder.some((field) => {
      if (errorObject[field]) {
        const errorElement = document.querySelector(`[name="${field}"]`)
        if (errorElement) {
          window.scrollBy({
            top: errorElement.getBoundingClientRect().top - 55,
            left: 0,
            behavior: 'smooth',
          })
          setTimeout(() => {
            errorElement.focus()
          }, 500)

          return true
        } else {
          return false
        }
      }
      return false
    })

    if (!errorFound) {
      const firstElement = document.querySelector('[name="personalnumber"]')
      if (firstElement) {
        window.scrollBy({
          top: firstElement.getBoundingClientRect().top - 55,
          left: 0,
          behavior: 'smooth',
        })
        setTimeout(() => {
          firstElement.focus()
        }, 500)
      }
    }
  }

  // SAME VALIDATION ON BE AND FE! If you change here, change on BE aswell
  Yup.addMethod(Yup.string, 'singlePersonalnumber', function (datas: Array<IStatisticsData>) {
    return this.test(
      'singlePersonalnumber',
      'Det finns dubbletter av detta personnummer.',
      function (value: string) {
        return (
          datas.filter((data) => value && data.personalnumber === value).length <=
          ((received && received.data
            ? received.data.find((e) => e.index === selectedIndex)
              ? received.data.find((e) => e.index === selectedIndex)
              : emptyStatisticsData
            : emptyStatisticsData
          )?.personalnumber === value
            ? 1
            : received?.imported
              ? 1
              : 0)
        )
      },
    )
  })

  Yup.addMethod(Yup.string, 'validPersonalNumber', function () {
    return this.test('validPersonalNumber', 'Personnumret är felaktigt.', function (value: string) {
      return Personnummer.valid(value)
    })
  })

  Yup.addMethod(Yup.string, 'validKlassigo', function () {
    return this.test(
      'validKlassigo',
      scb
        ? 'Obligatoriskt, Klassigo-kod saknas. Vid tveksamhet eller behov av stöd gällande klassificering, kontakta Fastigos statistiker på 08-676 69 23.'
        : 'Obligatoriskt, Klassigo-kod saknas. Vid tveksamhet eller behov av stöd gällande klassificering, kontakta Fastigos statistiker på 08-676 69 23.',
      function (value: string) {
        if (!value || value.length > 4) {
          return false
        }

        const firstNumber = value[0]
        let allowedSecondNumbers = []

        if (firstNumber === '1') {
          allowedSecondNumbers = ['1', '2']
        } else if (firstNumber === '2') {
          allowedSecondNumbers = ['1', '2', '3', '4', '5', '6']
        } else if (firstNumber === '3') {
          allowedSecondNumbers = ['1', '2', '3', '4']
        } else if (firstNumber === '4') {
          allowedSecondNumbers = ['1', '2', '3', '4', '5']
        } else if (firstNumber === '9') {
          allowedSecondNumbers = ['0', '9']
        } else {
          return false
        }

        const secondNumber = value[1]

        if (allowedSecondNumbers.indexOf(secondNumber) === -1) {
          return false
        }

        const allowedThirdNumbers = ['1', '2', '3', '4', '5', '6']
        const thirdNumber = value[2]

        if (allowedThirdNumbers.indexOf(thirdNumber) === -1) {
          if (firstNumber !== '9' || secondNumber !== '9' || thirdNumber !== '9') {
            return false
          }
        }

        const allowedFourthNumbers = ['0', '9']
        const fourthNumber = value[3]

        if (allowedFourthNumbers.indexOf(fourthNumber) === -1) {
          return false
        }

        return true
      },
    )
  })

  const updateValue = (
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    values: IStatisticsData,
    newValues: Array<IUpdateValueObject>,
  ) => {
    const originalDataClone = { ...currentData }
    for (const newValuesIndex in newValues) {
      const { name, value } = newValues[newValuesIndex]

      if ((values[name] || values[name] === 0) && !value.find((val) => val.id === values[name])) {
        setFieldValue(name, value[0].id)
      }

      originalDataClone[name] = value
    }

    setCurrentData(originalDataClone)
  }

  const handleDrop = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setFetchingData(true)

    setShowImportSuccessMsg(false)
    setShowImportErrorMsg(false)

    try {
      const worker = new window.Worker(`${process.env.PUBLIC_URL}/fileworker.js`)
      worker.onmessage = (e) => {
        const data = e.data
        const workbook = XLSX.read(data, { type: 'array', sheets: 0 })

        const importedData = XLSX.utils.sheet_to_json(workbook.Sheets.Mall, {
          range: 3,
          header: scb
            ? [
                'personalnumber',
                'name',
                'agreement',
                'klassigoCode',
                'klassigoSpecial',
                'occupationalDesignation',
                'employmentForm',
                'salaryForm',
                'fixedSalary',
                'extraSalary',
                'benefits',
                'resultSalary',
                'weeklyHours',
                'partTimePercentage',
                'workedHours',
                'overTimeHours',
                'overTimeSalary',
                'emergencyCompensation',
                'workAccount',
                'workAccountValue',
                'shorterWorkingHours',
                'vacationDays',
                'otherAdditions',
                'comment',
              ]
            : [
                'personalnumber',
                'name',
                'agreement',
                'klassigoCode',
                'klassigoSpecial',
                'occupationalDesignation',
                'fixedSalary',
                'extraSalary',
                'benefits',
                'resultSalary',
                'weeklyHours',
                'partTimePercentage',
                'workAccount',
                'workAccountValue',
                'shorterWorkingHours',
                'vacationDays',
                'comment',
              ],
        })
        let currentIndex = latestIndex

        const mappedImportData = importedData.map((el: IStatisticsData) => {
          currentIndex += 1

          if (!el.workAccount && el.workAccount !== 0) {
            el.workAccount = undefined
          }

          if (
            (el.workAccount || el.workAccount === 0) &&
            currentData.workAccount.findIndex((wa) => wa.id === el.workAccount) === -1
          ) {
            el.workAccount = '2'
          }

          if (!el.klassigoSpecial || el.klassigoSpecial === '0') {
            el.klassigoSpecial = ''
          }

          if (!el.agreement && el.agreement !== 0) {
            el.agreement = ''
          }

          if (!el.salaryForm || el.salaryForm === '0') {
            el.salaryForm = ''
          }

          if (!el.employmentForm || el.employmentForm === '0') {
            el.employmentForm = ''
          }

          if (el.fixedSalary && typeof el.fixedSalary !== 'number') {
            const value = el.fixedSalary.replace(/\D/g, '')
            el.fixedSalary = parseInt(value)
          }

          // if file imported has % the importer will convert it to a number and therfore we need to convert it back to %
          if (el.partTimePercentage <= 1) {
            el.partTimePercentage = el.partTimePercentage * 100
          }

          if (el.personalnumber) {
            el.personalnumber = el.personalnumber.toString()
            const personalnumberStart = el.personalnumber.substr(0, 2)
            if (personalnumberStart !== '20' && personalnumberStart !== '19') {
              let newPersonalnumber = ''
              if (parseInt(personalnumberStart) < 40) {
                newPersonalnumber = '20' + el.personalnumber
              } else if (parseInt(personalnumberStart) > 40) {
                newPersonalnumber = '19' + el.personalnumber
              }

              el.personalnumber = newPersonalnumber
            }

            const foundStringIndex = el.personalnumber.indexOf('-')
            if (foundStringIndex !== -1) {
              let newPersonalnumber = el.personalnumber

              newPersonalnumber = newPersonalnumber.split('-')
              newPersonalnumber = newPersonalnumber.join('')

              el.personalnumber = newPersonalnumber
            }
          }

          return { ...el, index: currentIndex - 1 }
        })

        if (resetter) {
          resetter()
        }

        window.localStorage.setItem('statisticsCache', JSON.stringify(mappedImportData))

        setReceived({
          data: mappedImportData,
          imported: true,
        })

        setLatestIndex(currentIndex)
      }

      worker.onerror = (e) => {
        console.error({ e })
      }
      const files = e.target.files
      const file = files[0]
      worker.postMessage(file)

      document.querySelector('#upload-button')?.scrollIntoView({
        behavior: 'smooth',
      })
    } catch (error) {
      setShowImportErrorMsg(true)
      setUploadMessage(
        'Det var något fel på din fil och vi kunde inte importera den. Vänligen försök igen.',
      )
    } finally {
      setFetchingData(false)
    }
  }

  const sendExtraEmail = async () => {
    setExtraEmailSentError(false)
    try {
      const res = await api.post(
        `/accounts/${selectedOrganization.id}/statistics/send-extra-email`,
        {
          email: extraConfirmationEmail,
        },
      )
      if (res.status === 200) {
        setExtraEmailSent(true)
      }
    } catch (e) {
      setExtraEmailSent(false)
      setExtraEmailSentError(true)
      console.log({ e })
    }
  }

  const submitStatistics = async (e) => {
    e.preventDefault()
    setFetchingData(true)
    setShowSubmitModal(false)

    setShowImportErrorMsg(false)

    try {
      const res = await api.post(`/accounts/${selectedOrganization.id}/statistics`, {
        statistics: received.data.filter((e) => e.agreement),
        scb,
      })

      const data = res.data
      setSubmitErrorMessage('')

      if (res.status === 200) {
        setAlreadySentStatistics(true)
        window.localStorage.setItem('statisticsCache', JSON.stringify([]))

        setReceived({
          data: [],
          imported: false,
        })

        // window.scrollTo({
        //   bottom: 0,
        //   left: 0,
        //   behavior: 'smooth',
        // })
      }

      if (res.status === 400) {
        if (data.folderCreation === false || data.fileCreation === false) {
          setSubmitErrorMessage('Något gick fel vid inskickningen, vänligen försök igen.')
        } else if (data.alreadySentStatistics) {
          setAlreadySentStatistics(true)
        } else {
          setSubmitErrorMessage(
            'Ditt inskick gick inte igenom, den innehåller en eller flera poster som är felaktiga.',
          )
        }
      }
      setFetchingData(false)
    } catch (e) {
      setSubmitErrorMessage('Något gick fel vid inskickningen, vänligen försök igen.')
      console.log({ e })
      setFetchingData(false)
      return []
    }
  }

  const getAccountData = async () => {
    if (selectedOrganization) {
      try {
        const res = await api.get(`/accounts/${selectedOrganization.id}/statistics`)
        const data = res.data

        setReceived({
          data: [],
          imported: false,
        })
        setDisplayForm(false)
        setScb(data.data.statistics === 'submit_scb_statistics')
        setAlreadySentStatistics(data.data.has_left_statsticas_year_c)
        setShouldNotSendStatistics(data.data.statistics === 'not_submit_statistics')
      } catch (e) {
        console.log({ e })
      }

      setFetchingData(false)
    }
  }

  const validateReceived = async () => {
    if (Array.isArray(received.data)) {
      const invalidData = received.data.filter((e) => !e.valid)
      const validData = received.data.filter((e) => e.valid)

      setHasValidData(validData.length > 0)
      setHasInvalidData(invalidData.length > 0)
    }

    if (Array.isArray(received.data) && received.imported) {
      const newData = []
      let invalidCases = false
      // let firstErrorFound = false
      let highestIndex = 0
      setFetchingData(true)
      setTimeout(async () => {
        for (const rowIndex in received.data) {
          const row = received.data[rowIndex]
          if (row.index > highestIndex) {
            highestIndex = row.index
          }

          try {
            await validationSchema(received, scb).validate(row)
            newData.push({ ...row, valid: true })
          } catch (e) {
            invalidCases = true
            newData.push({ ...row, valid: false, error: e.message })
          }
        }

        setSelectedIndex(highestIndex + 1)

        setFetchingData(false)
        setReceived({
          data: newData,
          imported: false,
        })

        if (invalidCases || newData.length === 0) {
          setShowImportErrorMsg(true)
          setUploadMessage('Otillräcklig uppladdning, vänligen uppdatera mallen och ladda upp igen')
        } else {
          setShowImportSuccessMsg(true)
          setUploadMessage(
            'Din uppladdning är korrekt! Du kan nu klicka på "Skicka in". OBS Spara din fil på en plats där du hittar den, då vi kan komma att höra av oss med frågor om er inlämnade statistik',
          )
        }
      }, 1)
    }
  }

  useEffect(() => {
    validateReceived()
  }, [received])

  useEffect(() => {
    setFetchingData(true)
    getAccountData()

    const originalDataClone = { ...originalData }

    originalDataClone.klassigoSpecial = [{ id: '0', name: '0' }]
    originalDataClone.subProcess = [
      { id: '1', name: '1' },
      { id: '2', name: '2' },
    ]
    originalDataClone.level = [
      { id: '1', name: '1' },
      { id: '2', name: '2' },
      { id: '3', name: '3' },
      { id: '4', name: '4' },
      { id: '5', name: '5' },
      { id: '6', name: '6' },
    ]

    if (scb) {
      originalDataClone.agreement = [
        { id: 'I', name: 'I = I-avtal (Fastigo / Unionen, Ledarna, AiF).' },
        { id: 'K', name: 'K = K-avtal (Fastigo / Vision, Ledarna, AiF).' },
        { id: 'S', name: 'S = S-avtal (Fastigo / Kommunal).' },
        { id: 'F', name: 'F = F-avtal (Fastigo / Fastighets).' },
        { id: 'M', name: 'M = Månadslöneavtal för målare (Fastigo / Svenska Målareförbundet).' },
        {
          id: 'L',
          name: 'L = Kollektivanställda som går under andra avtal med LO-förbund än S-, F-avtal och Månadslöneavtal för målare.',
        },
        {
          id: 'O',
          name: 'O = Tjänstemän, medarbetare och chefer (inklusive VD) som går under annat avtal än I- och K-avtal.',
        },
      ]
    } else {
      originalDataClone.agreement = [
        { id: 'I', name: 'I = I-avtal (Fastigo / Unionen, Ledarna, AiF).' },
        { id: 'K', name: 'K = K-avtal (Fastigo / Vision, Ledarna, AiF).' },
        { id: 'S', name: 'S = S-avtal (Fastigo / Kommunal).' },
        { id: 'F', name: 'F = F-avtal (Fastigo / Fastighets).' },
        { id: 'M', name: 'M = Månadslöneavtal för målare (Fastigo / Svenska Målareförbundet).' },
      ]
    }

    setCurrentData(originalDataClone)
  }, [])

  useEffect(() => {
    const originalDataClone = { ...originalData }

    if (scb) {
      originalDataClone.agreement = [
        { id: 'I', name: 'I = I-avtal (Fastigo / Unionen, Ledarna, AiF).' },
        { id: 'K', name: 'K = K-avtal (Fastigo / Vision, Ledarna, AiF).' },
        { id: 'S', name: 'S = S-avtal (Fastigo / Kommunal).' },
        { id: 'F', name: 'F = F-avtal (Fastigo / Fastighets).' },
        { id: 'M', name: 'M = Månadslöneavtal för målare (Fastigo / Svenska Målareförbundet).' },
        {
          id: 'L',
          name: 'L = Kollektivanställda som går under andra avtal med LO-förbund än S-, F-avtal och Månadslöneavtal för målare.',
        },
        {
          id: 'O',
          name: 'O = Tjänstemän, medarbetare och chefer (inklusive VD) som går under annat avtal än I- och K-avtal.',
        },
      ]
    } else {
      originalDataClone.agreement = [
        { id: 'I', name: 'I = I-avtal (Fastigo / Unionen, Ledarna, AiF).' },
        { id: 'K', name: 'K = K-avtal (Fastigo / Vision, Ledarna, AiF).' },
        { id: 'S', name: 'S = S-avtal (Fastigo / Kommunal).' },
        { id: 'F', name: 'F = F-avtal (Fastigo / Fastighets).' },
        { id: 'M', name: 'M = Månadslöneavtal för målare (Fastigo / Svenska Målareförbundet).' },
      ]
    }

    setCurrentData(originalDataClone)
  }, [scb])

  useEffect(() => {
    if (setter) {
      const newValues: Array<IUpdateValueObject> = []

      const code = outsideValues.klassigoCode && outsideValues.klassigoCode.toString()

      const headProcess = code && code[0] ? code[0] : ''
      const subProcess = code && code[1] ? code[1] : ''
      const level = code && code[2] ? code[2] : ''
      const boss = code && code[3] ? code[3] : ''

      switch (headProcess) {
        case '1':
          newValues.push({
            value: [
              { id: '1', name: '1' },
              { id: '2', name: '2' },
            ],
            name: 'subProcess',
          })
          break
        case '2':
          newValues.push({
            value: [
              { id: '1', name: '1' },
              { id: '2', name: '2' },
              { id: '3', name: '3' },
              { id: '4', name: '4' },
              { id: '5', name: '5' },
              { id: '6', name: '6' },
            ],
            name: 'subProcess',
          })
          break
        case '3':
          newValues.push({
            value: [
              { id: '1', name: '1' },
              { id: '2', name: '2' },
              { id: '3', name: '3' },
              { id: '4', name: '4' },
            ],
            name: 'subProcess',
          })
          break
        case '4':
          newValues.push({
            value: [
              { id: '1', name: '1' },
              { id: '2', name: '2' },
              { id: '3', name: '3' },
              { id: '4', name: '4' },
              { id: '5', name: '5' },
            ],
            name: 'subProcess',
          })
          break
        case '9':
          newValues.push({
            value: [{ id: '9', name: '9' }],
            name: 'subProcess',
          })
          break
      }

      if (headProcess === '2' && subProcess === '3' && level === '1') {
        newValues.push({
          value: [
            { id: '1', name: '1 = Mer än halva arbetstiden lokalvård' },
            { id: '2', name: '2 = Mer än halva arbetstiden annat än lokalvård.' },
          ],
          name: 'klassigoSpecial',
        })
      } else {
        newValues.push({
          value: [{ id: '0', name: '0' }],
          name: 'klassigoSpecial',
        })
      }

      if (headProcess === '9' && subProcess === '9') {
        newValues.push({
          value: [
            { id: '1', name: '1' },
            { id: '2', name: '2' },
            { id: '3', name: '3' },
            { id: '4', name: '4' },
            { id: '5', name: '5' },
            { id: '6', name: '6' },
            { id: '9', name: '9' },
          ],
          name: 'level',
        })
      } else {
        newValues.push({
          value: [
            { id: '1', name: '1' },
            { id: '2', name: '2' },
            { id: '3', name: '3' },
            { id: '4', name: '4' },
            { id: '5', name: '5' },
            { id: '6', name: '6' },
          ],
          name: 'level',
        })
      }

      updateValue(setter, outsideValues, newValues)
      setter('headProcess', headProcess)
      setter('subProcess', subProcess)
      setter('level', level)
      setter('boss', boss)
    }
  }, [outsideValues.klassigoCode])

  useEffect(() => {
    if (setter) {
      const { personalnumber } = outsideValues

      if (personalnumber.length > 2) {
        const personalnumberStart = personalnumber.substr(0, 2)
        if (personalnumberStart !== '20' && personalnumberStart !== '19') {
          let newPersonalnumber = ''
          if (parseInt(personalnumberStart) < 40) {
            newPersonalnumber = '20' + personalnumber
          } else if (parseInt(personalnumberStart) > 40) {
            newPersonalnumber = '19' + personalnumber
          }

          setter('personalnumber', newPersonalnumber)
        }

        const foundStringIndex = personalnumber.indexOf('-')
        if (foundStringIndex !== -1) {
          let newPersonalnumber = personalnumber

          newPersonalnumber = newPersonalnumber.split('-')
          newPersonalnumber = newPersonalnumber.join('')

          setter('personalnumber', newPersonalnumber)
        }
      }
    }
  }, [outsideValues.personalnumber])

  useEffect(() => {
    getAccountData()
  }, [selectedOrganization])

  useEffect(() => {
    if (submitter) {
      scrollToFirstErrorField()
    }

    if (resetter) {
      resetter()
    }

    if (!received.data.find((e) => e.index === selectedIndex)) {
      setDisplayForm(false)
    } else {
      setDisplayForm(true)
    }
  }, [selectedIndex])

  interface IUpdateValueObject {
    value: Array<IStatisticsSelectData>
    name: string
  }

  if (shouldNotSendStatistics) {
    return (
      <div className="card flex flex-col w-full bg-white justify-center items-center">
        <h3 className="text-2xl mt-4 font-bold text-black">Du behöver inte skicka in statistik</h3>
        <button className="btn-white mt-8" onClick={() => navigate('/')}>
          <span className="pt-1"> Tillbaka till översikt</span>
        </button>
      </div>
    )
  }

  if (alreadySentStatistics) {
    return (
      <div className="card flex flex-col w-full bg-white justify-center items-center">
        <svg
          width="62"
          height="61"
          viewBox="0 0 62 61"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="31"
            cy="30.1035"
            r="29.6117"
            fill="#F9F9F9"
            stroke="#BFCED3"
            stroke-width="0.983607"
          />
          <path
            d="M31.2989 50.1936C19.4231 50.1936 9.79639 40.9681 9.79639 29.587C9.79639 18.206 19.4231 8.98047 31.2989 8.98047C43.1748 8.98047 52.8014 18.206 52.8014 29.587C52.8014 40.9681 43.1748 50.1936 31.2989 50.1936ZM29.1551 37.8297L44.3574 23.2588L41.3191 20.345L29.1551 32.0021L23.072 26.1725L20.0316 29.0863L29.1551 37.8297Z"
            fill="#057D63"
          />
        </svg>
        <h3 className="text-2xl mt-4 font-bold text-black">
          Tack för att du har skickat in lönestatistik för 2026!
        </h3>
        <p className="text-darkBlue">
          Ett bekräftelsemail har skickats till din angivna mailadress.
        </p>

        {received.data && (
          <div className="pt-12 pb-8 w-full flex justify-center">
            {!extraEmailSent && (
              <div className=" w-full " style={{ maxWidth: '716px' }}>
                <p style={{ fontSize: '16px' }}>Om du vill har du möjlighet att fylla i ytterligare en mailadress att skicka en bekräftelse till (valfritt):</p>
                <div className="flex flex-row items-center gap-2 w-full">
                  <input
                    type="text"
                    className="input mt-3"
                    placeholder="Ange e-postadress"
                    onChange={(event) => setExtraConfirmationEmail(event.target.value)}
                    value={extraConfirmationEmail}
                  />
                  <button className="btn mt-3" style={{ height: '50px' }} onClick={sendExtraEmail}>
                    Skicka
                  </button>
                </div>
              </div>
            )}
            {extraConfirmationEmail && extraEmailSent && (
              <p style={{ fontSize: '16px' }}>
                {' '}
                Bekräftelsemail har skickats till {extraConfirmationEmail}{' '}
              </p>
            )}
            {extraEmailSentError && <p style={{ fontSize: '16px' }}>Något gick fel, vänligen försök igen!</p>}
          </div>
        )}
        <button className="btn-white mt-8" onClick={() => navigate('/')}>
          <span className="pt-1"> Tillbaka till översikt</span>
        </button>
      </div>
    )
  }

  return (
    <div className="card flex flex-col w-full bg-white">
      <StatisticsInfo alreadySentStatistics={alreadySentStatistics} />

      <div className="py-4 flex flex-col md:flex-row md:py-8 md:justify-between">
        {fetchingData && <CoverEverythingSpinner />}
        {!fetchingData && (
          <div className="w-full">
            <div className="md:card flex flex-col gap-12">
              <div className="border-bottom-grey flex flex-col gap-6 justify-start items-start pt-0 pb-12">
                <h2>Steg 1: Läs anvisningar för statistikinlämmning</h2>
                <p className="my-2">Vänligen läs anvisningarna noga innan ni börjar.</p>
                <a
                  className="btn gap-4"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  href={scb ? downloadSCBHelplink : downloadNoSCBHelplink}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  Anvisningar för statistikinlämmning (PDF)
                </a>
              </div>

              <div className="flex flex-col gap-6 justify-start items-start border-bottom-grey pb-12">
                <h2>Steg 2: Ladda ner mall</h2>
                <p className="my-2">
                  Ladda ner årets Excel-mall och fyll i den enligt anvisningarna.
                </p>
                <a
                  className="btn gap-4"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  href={scb ? downloadSCBMalllink : downloadNoSCBMalllink}
                >
                  <FontAwesomeIcon icon={faDownload} />Mall Lönestatistik för 2026
                </a>
              </div>

              <div>
                <div className="flex flex-col">
                  <div className="flex flex-col gap-6 justify-start items-start border-bottom-grey pb-12">
                    <div className="flex flex-col gap-6 justify-start items-start">
                      <h2>Steg 3: Ladda upp ifylld mall</h2>
                      <p className="my-2">Ladda upp den ifyllda Excel-mallen till portalen.</p>
                    </div>
                    <div className="flex flex-col items-start">
                      <label htmlFor="importfile" className="btn w-fit gap-4" id="upload-button">
                        <FontAwesomeIcon icon={faUpload} /> Ladda upp lönestatistik
                      </label>
                      <input
                        id="importfile"
                        type="file"
                        onDrop={handleDrop}
                        onChange={handleDrop}
                        style={{
                          width: '0.1',
                          height: '0.1',
                          opacity: 0,
                          overflow: 'hidden',
                          zIndex: -1,
                          position: 'absolute',
                        }}
                      />
                      {showImportErrorMsg && (
                        <div>
                          <p className="error-message  pb-0 text-sm font-bold">{uploadMessage}</p>
                        </div>
                      )}

                      {showImportSuccessMsg && (
                        <p className="success-message pb-0 text-sm font-bold"> {uploadMessage}</p>
                      )}
                    </div>
                  </div>

                  <EditModal
                    displayForm={displayForm}
                    setDisplayForm={setDisplayForm}
                    received={received}
                    selectedIndex={selectedIndex}
                    latestIndex={latestIndex}
                    setReceived={setReceived}
                    setSelectedIndex={setSelectedIndex}
                    scb={scb}
                    setOutsideValues={setOutsideValues}
                    currentData={currentData}
                  />

                  <div className="pt-12">
                    <div className="flex flex-col border-t border-lightGray">
                      <div className="flex flex-col md:flex-row items-start justify-between">
                        <div className="flex flex-col gap-6 justify-start items-start w-full md:w-2/3">
                          <h2 id="savedImports">
                            Steg 4: Kontrollera poster <br />
                            Eventuella korrigeringar görs i Excel-filen <br /> och laddas sedan upp
                            på nytt.
                          </h2>
                          <p className="my-2  w-full md:w-5/6">
                            Vid felaktiga poster, klicka på "Läs mer" och se vilken information som
                            behöver korrigeras för varje post. Gör ändringarna i din Excel-fil och
                            ladda sedan upp filen på nytt.
                          </p>
                        </div>
                        {(hasValidData || hasInvalidData) && (
                          <button
                            className="btn-white w-56 gap-2"
                            onClick={(event) => {
                              event.preventDefault()
                              if (
                                window.confirm('Är det säkert att du vill ta bort samtliga poster?')
                              ) {
                                window.localStorage.setItem('statisticsCache', JSON.stringify([]))

                                setReceived({
                                  data: [],
                                  imported: false,
                                })
                                setDisplayForm(false)
                                setUploadMessage('')
                              }
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Rensa alla poster
                          </button>
                        )}
                      </div>

                      <div className="flex flex-row gap-6 mt-6">
                        {!hasInvalidData && !hasValidData && (
                          <div className="w-full flex flex-col items-center justify-center card-grey p-12">
                            <p className="font-bold ">Ingen fil uppladdad ännu</p>
                          </div>
                        )}
                        {(hasValidData || hasInvalidData) && (
                          <div className="flex flex-col md:flex-row gap-6 mt-6 w-full">
                            <div className="w-full md:w-6/12 flex flex-col card-grey p-6">
                              <span className="text-darkGreen font-bold text-xl">
                                Godkända poster{' '}
                                <span>
                                  {received && received.data && Array.isArray(received.data)
                                    ? received.data.filter((e) => e.valid).length
                                    : 0}
                                  st
                                </span>
                              </span>
                              {received &&
                                received.data &&
                                Array.isArray(received.data) &&
                                received.data.map((post, index) => (
                                  <span key={index}>
                                    {post.valid && (
                                      <div className="flex flex-row items-center justify-between border-bottom-grey ">
                                        <span className="flex flex-row items-center justify-between gap-2">
                                          <FontAwesomeIcon
                                            className="text-darkGreen"
                                            icon={faCheckCircle}
                                          />
                                          {post.personalnumber ?? 'X'}
                                        </span>
                                        <button
                                          type="button"
                                          className="py-2 px-4 font-bold text-darkBlue cursor-pointer read-more"
                                          onClick={() => {
                                            if (selectedIndex === post.index) {
                                              if (submitter) {
                                                scrollToFirstErrorField()
                                              }
                                              if (resetter) {
                                                resetter()
                                              }
                                              if (
                                                !received.data.find(
                                                  (item) => item.index === selectedIndex,
                                                )
                                              ) {
                                                setDisplayForm(false)
                                              } else {
                                                setDisplayForm(true)
                                              }
                                            } else {
                                              setSelectedIndex(post.index)
                                            }
                                          }}
                                        >
                                          &nbsp;
                                        </button>
                                      </div>
                                    )}
                                  </span>
                                ))}
                            </div>
                            <div className="w-full md:w-6/12 flex flex-col card-grey p-6">
                              <span className="text-red font-bold text-xl">
                                Felaktiga poster{' '}
                                <span className=" text-base">
                                  {received && received.data && Array.isArray(received.data)
                                    ? received.data.filter((e) => !e.valid).length
                                    : 0}
                                  st
                                </span>
                              </span>
                              {received &&
                                received.data &&
                                Array.isArray(received.data) &&
                                received.data.map((post, index) => (
                                  <span key={index}>
                                    {!post.valid && (
                                      <div className="flex flex-row items-center justify-between border-bottom-grey ">
                                        <span className="flex flex-row items-center justify-center gap-2">
                                          <FontAwesomeIcon
                                            className="text-red"
                                            icon={faXmarkCircle}
                                          />
                                          {post.personalnumber ?? 'X'}
                                        </span>

                                        <button
                                          type="button"
                                          className="py-2 px-4 font-bold text-darkBlue cursor-pointer read-more"
                                          onClick={() => {
                                            if (selectedIndex === post.index) {
                                              if (submitter) {
                                                scrollToFirstErrorField()
                                              }
                                              if (resetter) {
                                                resetter()
                                              }
                                              if (
                                                !received.data.find(
                                                  (item) => item.index === selectedIndex,
                                                )
                                              ) {
                                                setDisplayForm(false)
                                              } else {
                                                setDisplayForm(true)
                                              }
                                            } else {
                                              setSelectedIndex(post.index)
                                            }
                                          }}
                                        >
                                          &nbsp;
                                        </button>
                                      </div>
                                    )}
                                  </span>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex flex-col md:flex-row items-end justify-between w-full md:gap-12 gap-2">
                  <button className="btn-white w-full" onClick={() => setStep(1)}>
                    Avbryt
                  </button>
                  <div className="flex flex-col items-end w-full">
                    {hasInvalidData && (
                      <p className=" error-message text-center text-sm font-bold">
                        Det finns felaktiga poster - korrigera dessa i din Excel-fil och ladda upp
                        filen på nytt
                      </p>
                    )}
                    {submitErrorMessage && (
                      <p className=" error-message text-center text-sm font-bold">
                        {submitErrorMessage}
                      </p>
                    )}
                    <button
                      className="btn w-full"
                      onClick={() => setShowSubmitModal(true)}
                      disabled={hasInvalidData || !hasValidData}
                    >
                      Skicka in
                    </button>

                    <Modal
                      isOpen={showSubmitModal}
                      style={{
                        content: {
                          maxWidth: '640px',
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          marginTop: 'auto',
                          marginBottom: 'auto',
                          maxHeight: '300px',
                          padding: 12,
                        },
                      }}
                    >
                      <div className="modal">
                        <h3 className="text-2xl mt-4 font-bold">
                          Vill du skicka in lönestatistiken?
                        </h3>
                        <p>
                          Kom ihåg att spara din fil på en plats där du hittar den, då vi kan komma
                          att höra av oss med frågor om er inlämnade statistik.
                        </p>
                        <div className="modal-btn-wrapper">
                          <button
                            className="btn-white"
                            onClick={() => setShowSubmitModal(false)}
                            disabled={hasInvalidData || !hasValidData}
                          >
                            Avbryt
                          </button>
                          <button
                            className="btn"
                            onClick={submitStatistics}
                            disabled={hasInvalidData || !hasValidData}
                          >
                            Skicka in
                          </button>
                        </div>
                      </div>
                    </Modal>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Statistics
